import TeamSelectDeprecated from "@/components/messaging/TeamSelect";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton
} from "@velaro/velaro-component-lib";
import React, { useEffect, useState } from "react";
import { SmsIntegrationModel } from "../types";
import { WorkflowListItem } from "../Workflows/types";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";

interface Props {
  onClose(): void;
  release(): Promise<void>;
  update(integration: SmsIntegrationModel): void;
  integration: SmsIntegrationModel;
  error: string;
}

type WorkflowStatus = "active" | "disabled" | "missing" | "none";

function workflowStatus(integration: SmsIntegrationModel): WorkflowStatus {
  if (!integration.workflowId) return "none";
  if (!integration.workflowExists) return "missing";
  if (integration.workflowEnabled === false) return "disabled";
  return "active";
}

function WorkflowStatusBadge({ status }: { status: WorkflowStatus }) {
  const styles: Record<WorkflowStatus, string> = {
    active: "bg-green-50 text-green-700 border border-green-200",
    disabled: "bg-amber-50 text-amber-700 border border-amber-200",
    missing: "bg-red-50 text-red-700 border border-red-200",
    none: "bg-slate-100 text-slate-500 border border-slate-200"
  };
  const labels: Record<WorkflowStatus, string> = {
    active: "Active",
    disabled: "Disabled",
    missing: "Deleted",
    none: "Not assigned"
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

export default function SmsIntegrationEditModal(props: Props) {
  const [selectedTeam, setSelectedTeam] = useState(props.integration.teamId);
  const [selectedWorkflow, setSelectedWorkflow] = useState<number | undefined>(
    props.integration.workflowId
  );
  const [workflows, setWorkflows] = useState<WorkflowListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const api = useApi();

  useEffect(() => {
    fetchWorkflows();
  }, []);

  async function fetchWorkflows() {
    try {
      const response = await api.messaging.get("Workflows/List");
      const data: WorkflowListItem[] = await response.json();
      setWorkflows(Array.isArray(data) ? data : []);
    } catch {
      setWorkflows([]);
    }
  }

  async function handleRelease() {
    setLoading(true);
    await props.release();
    setLoading(false);
  }

  const currentStatus = workflowStatus({
    ...props.integration,
    workflowId: selectedWorkflow,
    workflowExists: selectedWorkflow
      ? (selectedWorkflow === props.integration.workflowId
          ? props.integration.workflowExists
          : true)
      : undefined,
    workflowEnabled: selectedWorkflow === props.integration.workflowId
      ? props.integration.workflowEnabled
      : true
  });

  return (
    <Modal show={true}>
      <ModalHeader
        title={`Edit SMS Number — ${props.integration.phoneNumber}`}
        onClose={() => props.onClose()}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          props.update({
            ...props.integration,
            teamId: selectedTeam,
            workflowId: selectedWorkflow
          });
        }}
      >
        <ModalBody>
          <div className="p-5 space-y-5">

            {/* Warnings */}
            {currentStatus === "disabled" && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start gap-2">
                <span className="mt-0.5 text-amber-500">⚠</span>
                <div>
                  <strong>Workflow is disabled.</strong> Texts to this number will still come in,
                  but the assigned workflow (<em>{props.integration.workflowName}</em>) is
                  turned off. Re-enable it in <strong>Messaging → Workflows</strong>, or
                  reassign below.
                </div>
              </div>
            )}
            {currentStatus === "missing" && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 flex items-start gap-2">
                <span className="mt-0.5 text-red-500">✕</span>
                <div>
                  <strong>Assigned workflow was deleted.</strong> New conversations from this
                  number will have no automation. Please assign a new workflow below.
                </div>
              </div>
            )}

            {/* Phone number (read-only) */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
              <div className="flex items-center gap-2">
                <span className="text-slate-700 font-mono text-sm bg-slate-50 border border-slate-200 rounded px-3 py-1.5">
                  {props.integration.phoneNumber}
                </span>
                <span className="text-xs text-slate-400">Read-only</span>
              </div>
            </div>

            {/* Team */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Team</label>
              <TeamSelectDeprecated
                id="sms-team"
                className="input w-full"
                onChange={(e) => setSelectedTeam(e.currentTarget.value)}
                value={selectedTeam}
              />
              <p className="mt-1 text-xs text-slate-400">
                The team that receives new conversations from this number.
              </p>
            </div>

            {/* Workflow */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-2">
                Auto-start Workflow
                <WorkflowStatusBadge status={currentStatus} />
              </label>
              <select
                id="sms-workflow"
                className="input w-full"
                onChange={(e) =>
                  setSelectedWorkflow(
                    e.currentTarget.value ? Number(e.currentTarget.value) : undefined
                  )
                }
                value={selectedWorkflow ?? ""}
              >
                <option value="">— No workflow —</option>
                {workflows.map((wf) => (
                  <option key={wf.id} value={wf.id}>
                    {wf.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-400">
                When a new conversation starts on this number, this workflow runs automatically.
                Leave blank to receive conversations without automation.
              </p>
            </div>

            {props.error && (
              <div className="text-red-600 text-sm">{props.error}</div>
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <div className="p-4 flex items-center justify-between">
            <button
              type="button"
              className="btn bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-md text-sm font-medium"
              disabled={loading}
              onClick={handleRelease}
            >
              Release Number
            </button>
            <div className="flex items-center gap-2">
              {loading ? (
                <span className="text-sm text-slate-500 flex items-center gap-1">
                  Saving… <LoadingSpinner />
                </span>
              ) : (
                <PrimaryBlueButton type="submit" label="Save Changes" disabled={loading} />
              )}
            </div>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
}
